'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    ChangeEvent
} from 'react';
import {useSelector} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';

import {
    InputNumber,
    InputNumberProps,
    InputNumberChangeEvent
} from 'primereact/inputnumber';
import {
    InputText,
    InputTextProps
} from 'primereact/inputtext';
import {SpeedDial} from 'primereact/speeddial';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    CoordinateFieldPresentation,
    useHookFormDelay
} from '~/shared/src/utilities/modules/form';
import {LayerType as MapLayerType} from '~/shared/src/utilities/map';

import SearchField from '~/frontend/src/components/base/shared/field/SearchField';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';

import FieldWrapper, {
    defaultProps as fieldWrapperDefaultProps,
    componentProps as fieldWrapperComponentProps,
    ComponentProps as FieldWrapperComponentProps,
    Props as FieldWrapperProps,
    DefaultProps as FieldWrapperDefaultProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import Map, {
    Marker,
    ReferenceType as MapReferenceType
} from '~/frontend/src/components/base/Map';

import {StoreState} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';


type InputTextChangeProps = ChangeEvent<HTMLInputElement>;


const inputNumberProps = keys<Omit<InputNumberProps, 'onChange' | 'key'>>(),
      inputTextProps   = keys<Omit<InputTextProps, 'onChange' | 'key'>>();


type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The value.
     */
    value? : number[];

    /**
     * The value to determine dirty against.
     */
    originalValue? : number[];

    /**
     * The number of text rows to be displayed within the field.
     * Set to 0 to fit all content or reach maxHeight.
     */
    height? : number;

    /**
     * The max number of text rows to be displayed within the field.
     * Set to 0 for unlimited height.
     */
    maxHeight? : number;

    presentation? : CoordinateFieldPresentation;

    mapLayerType? : MapLayerType;

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};

const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    value          : [],
    originalValue  : [],
    presentation   : 'field',
    mapLayerType   : 'standard',
    height         : 6,
    maxHeight      : 0,
    rawMode        : false,
    markDirty      : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};


type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : [ number, number ],

    /**
     * The value to determine dirty against.
     */
    originalValue? : [ number, number ],

    onChange : ( value : [ number, number ] | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : [ number, number ] | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };

    onBlur? : ( event : FocusEvent ) => void;
    onFocus? : ( event : FocusEvent ) => void;
};

export type ComponentProps =
    Omit<( InputNumberProps | InputTextProps ), 'value' | 'onChange' | 'onFocus' | 'onBlur'>
    & DefaultProps
    & Props;
export const componentProps = keys<ComponentProps>();

type AllProps =
    Omit<( InputNumberProps | InputTextProps ), 'onChange'>
    & Omit<FieldWrapperComponentProps, 'children' | 'pt'>
    & Required<DefaultProps>
    & Props;


const CoordinateField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const user                                      = useSelector( ( state : StoreState ) => state.session?.user ),
              {
                  disabled,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  mapLayerType,
                  markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  presentation,
                  pt,
                  rawMode,
                  uid,
                  useHookForm,
                  value
              }                                         = props,
              elementId                                 = id ?? `${uid}_coordinateField`,
              inValue                                   = useRef<[ number, number ] | undefined>( value ),
              outValue                                  = useRef<[ number, number ] | undefined>( value ),
              timeoutId                                 = useRef<NodeJS.Timeout | undefined>(),
              map                                       = useRef<MapReferenceType | null>( null ),
              currentDial                               = useRef<SpeedDial | null>( null ),
              searchDial                                = useRef<SpeedDial | null>( null ),
              currentDialVisible                        = useRef<boolean>( false ),
              searchDialVisible                         = useRef<boolean>( false ),
              lastSearchString                          = useRef<string>( '' ),
              [ elementValue, setElementValue ]         = useState<[ number, number ] | undefined>( value ),
              [ focus, setFocus ]                       = useState<boolean>( false ),
              [ resolvedAddress, setResolvedAddress ]   = useState<string>( '' ),
              [ searchString, setSearchString ]         = useState<string>( '' ),
              [ searchCoordinate, setSearchCoordinate ] = useState<[ number, number ] | undefined>( undefined ),
              [ searchFocus, setSearchFocus ]           = useState<boolean>( false ),
              heightStyle                               = useMemo(
                  () => {
                      const retVal = getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout );

                      if ( presentation === 'map' && !retVal.minHeight ) {
                          retVal.minHeight = '5rem';
                      }

                      return retVal;
                  },
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                           = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                       = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              onInputChange                             = useCallback(
                  ( event : InputTextChangeProps ) => {
                      const changedValue = event.target.value
                          ? event.target.value.split( ',' ).map( v => Number( v ) ) as [ number, number ]
                          : undefined;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange                     = useCallback(
                  ( event : InputTextChangeProps ) => {
                      const changedValue = event.target.value
                          ? event.target.value.split( ',' ).map( v => Number( v ) ) as [ number, number ]
                          : undefined;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onLatitudeInputChange                     = useCallback(
                  ( event : InputNumberChangeEvent ) => {
                      const changedValue = [ event.value ?? 0, elementValue?.[ 1 ] ?? 0 ] as [ number, number ];

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormLatitudeInputChange             = useCallback(
                  ( event : InputNumberChangeEvent ) => {
                      const changedValue = [ event.value ?? 0, elementValue?.[ 1 ] ?? 0 ] as [ number, number ];

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onLongitudeInputChange                    = useCallback(
                  ( event : InputNumberChangeEvent ) => {
                      const changedValue = [ elementValue?.[ 0 ] ?? 0, event.value ?? 0 ] as [ number, number ];

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormLongitudeInputChange            = useCallback(
                  ( event : InputNumberChangeEvent ) => {
                      const changedValue = [ elementValue?.[ 0 ] ?? 0, event.value ?? 0 ] as [ number, number ];

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onInputFocus                              = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                               = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur
                  ]
              ),
              onSearchChange                            = useCallback(
                  ( searchStringToSet : string ) => {
                      setSearchString( searchStringToSet );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onSearchClear                             = useCallback(
                  () => {
                      setSearchString( '' );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onSearch                                  = useCallback(
                  () => {
                      if ( searchString && searchString !== lastSearchString.current ) {
                          const doFnc = async () => {
                              const response = await xhr.get(
                                  `https://nominatim.openstreetmap.org/search?q=${searchString}&limit=1&format=jsonv2`,
                                  {
                                      withCredentials: false
                                  },
                                  uid,
                                  'search',
                                  undefined,
                                  undefined,
                                  true
                              );

                              let searchCoordinateToSet : [ number, number ] | undefined = undefined;

                              if ( Array.isArray( response ) && response.length > 0 ) {
                                  searchCoordinateToSet = [ Number( response[ 0 ].lat ), Number( response[ 0 ].lon ) ];
                              }

                              setSearchCoordinate( searchCoordinateToSet );
                              lastSearchString.current = searchString;

                              setTimeout( () => {
                                  map.current?.fitBounds();
                              }, 15 );
                          };

                          void doFnc();
                      }
                  },
                  [
                      searchString,
                      uid
                  ]
              ),
              onSearchFocus                             = useCallback(
                  () => {
                      setSearchFocus( true );
                      setFocus( true );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onSearchBlur                              = useCallback(
                  () => {
                      setSearchFocus( false );
                      setFocus( false );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onCurrentMarkerClick                      = useCallback(
                  ( event : MouseEvent ) => {
                      if ( currentDial.current ) {
                          const tBox = ( event.target as HTMLElement ).getBoundingClientRect(),
                                el   = currentDial.current.getElement(),
                                dBox = el.getBoundingClientRect(),
                                pBox = el.parentElement?.getBoundingClientRect();

                          el.style.left = `${tBox.left + tBox.width / 2 - dBox.width / 2 - ( pBox?.left ?? 0 )}px`;
                          el.style.top = `${tBox.top + tBox.height / 2 - dBox.height / 2 - ( pBox?.top ?? 0 )}px`;

                          setTimeout( () => {
                              if ( currentDialVisible.current ) {
                                  currentDialVisible.current = false;
                                  currentDial.current?.hide();
                              }
                              else {
                                  searchDialVisible.current = false;
                                  currentDialVisible.current = true;
                                  currentDial.current?.show();
                              }
                          }, 15 );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onSearchMarkerClick                       = useCallback(
                  ( event : MouseEvent ) => {
                      if ( searchDial.current ) {
                          const tBox = ( event.target as HTMLElement ).getBoundingClientRect(),
                                el   = searchDial.current.getElement(),
                                dBox = el.getBoundingClientRect(),
                                pBox = el.parentElement?.getBoundingClientRect();

                          el.style.left = `${tBox.left + tBox.width / 2 - dBox.width / 2 - ( pBox?.left ?? 0 )}px`;
                          el.style.top = `${tBox.top + tBox.height / 2 - dBox.height / 2 - ( pBox?.top ?? 0 )}px`;

                          setTimeout( () => {
                              if ( searchDialVisible.current ) {
                                  searchDialVisible.current = false;
                                  searchDial.current?.hide();
                              }
                              else {
                                  currentDialVisible.current = false;
                                  searchDialVisible.current = true;
                                  searchDial.current?.show();
                              }
                          }, 15 );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onMapCoordinate                           = useCallback(
                  ( coordinate : [ number, number ] ) => {
                      const resolver = async () => {
                          const rA = await resolveAddress( coordinate );

                          setSearchString( rA );
                          lastSearchString.current = rA;
                      }

                      setSearchCoordinate( coordinate );
                      void resolver();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              resolveAddress                            = async ( coordinate : [ number, number ] ) => {
                  let retVal : string = '';

                  if ( presentation === 'map' && coordinate && coordinate.length === 2 ) {
                      const response = await xhr.get(
                          `https://nominatim.openstreetmap.org/reverse?lat=${coordinate[ 0 ]}&lon=${coordinate[ 1 ]}&format=jsonv2`,
                          {
                              withCredentials: false
                          },
                          uid,
                          'resolve',
                          undefined,
                          undefined,
                          true
                      ) as any;

                      retVal = response?.display_name ?? '';

                      if ( retVal && response?.name ) {
                          retVal = retVal.replace( `${response.name}, `, '' );
                      }
                  }

                  return retVal;
              },
              currentMarker                             = useMemo(
                  () => {
                      let retVal : Marker | undefined = undefined;

                      if ( presentation === 'map' && elementValue && elementValue.length === 2 ) {
                          const icon = document.createElement( 'span' );

                          icon.id = 'marker_current';
                          icon.className = 'yn-marker-icon material-symbols-outlined material-symbols-fill yn-map-current-marker';
                          icon.style.color = '#303F9F';
                          icon.appendChild( document.createTextNode( 'location_on' ) );
                          icon.addEventListener( 'click', onCurrentMarkerClick );

                          retVal = {
                              id            : 'marker_current',
                              rootId        : 'current',
                              position      : elementValue,
                              icon,
                              tooltipContent: __(
                                  'YN.base.form.coordinateField.positionMarker.current.tooltip',
                                  {
                                      address   : resolvedAddress,
                                      coordinate: elementValue.join( ', ' ),
                                      lat       : String( elementValue[ 0 ] ),
                                      lon       : String( elementValue[ 1 ] )
                                  }
                              )
                          };
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      onCurrentMarkerClick,
                      presentation,
                      resolvedAddress
                  ]
              ),
              searchMarker                              = useMemo(
                  () => {
                      let retVal : Marker | undefined = undefined;

                      if ( presentation === 'map' && searchCoordinate ) {
                          const icon = document.createElement( 'span' );

                          icon.id = 'marker_search';
                          icon.className = 'yn-marker-icon material-symbols-outlined material-symbols-fill yn-map-search-marker';
                          icon.style.color = '#E64A19';
                          icon.appendChild( document.createTextNode( 'my_location' ) );
                          icon.addEventListener( 'click', onSearchMarkerClick );

                          retVal = {
                              id            : 'marker_search',
                              rootId        : 'search',
                              position      : searchCoordinate,
                              icon,
                              tooltipContent: __(
                                  'YN.base.form.coordinateField.positionMarker.search.tooltip',
                                  {
                                      address   : searchString,
                                      coordinate: searchCoordinate.join( ', ' ),
                                      lat       : String( searchCoordinate[ 0 ] ),
                                      lon       : String( searchCoordinate[ 1 ] )
                                  }
                              )
                          };
                      }

                      return retVal;
                  },
                  [
                      onSearchMarkerClick,
                      presentation,
                      searchCoordinate,
                      searchString
                  ]
              ),
              currentDialEl                             = useMemo(
                  () => {
                      if ( presentation === 'map' ) {
                          const menuItems : MenuItem[] = [
                              {
                                  label    : __( 'YN.base.form.coordinateField.contextMenu.delete' ),
                                  icon     : 'pi pi-minus',
                                  className: 'yn-map-dial-button yn-map-dial-button-delete',
                                  command  : ( event : MenuItemCommandEvent ) => {
                                      if ( outValue.current !== undefined ) {
                                          setElementValue( undefined );

                                          if ( useHookForm ) {
                                              onIntermediateChange?.( undefined, event.originalEvent );

                                              if ( timeoutId.current ) {
                                                  clearTimeout( timeoutId.current );
                                              }
                                              timeoutId.current = setTimeout( onChange.bind( null, undefined, event.originalEvent ), useHookFormDelay );

                                          }
                                          else {
                                              onChange( undefined, event.originalEvent );
                                          }
                                      }
                                  }
                              }
                          ];

                          if ( JSON.stringify( originalValue ) !== JSON.stringify( outValue.current ) ) {
                              menuItems.push( {
                                  label  : __( 'YN.base.form.coordinateField.contextMenu.restore' ),
                                  icon   : 'pi pi-replay',
                                  command: ( event : MenuItemCommandEvent ) => {
                                      if ( outValue.current !== originalValue ) {
                                          if ( originalValue && originalValue.length === 2 ) {
                                              const resolver = async () => {
                                                  const rA = await resolveAddress( originalValue );

                                                  setResolvedAddress( rA );
                                                  setSearchString( rA );
                                                  lastSearchString.current = rA;
                                              };

                                              void resolver();
                                          }

                                          setElementValue( originalValue );

                                          if ( useHookForm ) {
                                              onIntermediateChange?.( originalValue, event.originalEvent );

                                              if ( timeoutId.current ) {
                                                  clearTimeout( timeoutId.current );
                                              }
                                              timeoutId.current = setTimeout( onChange.bind( null, originalValue, event.originalEvent ), useHookFormDelay );

                                          }
                                          else {
                                              onChange( originalValue, event.originalEvent );
                                          }
                                      }
                                  }
                              } );
                          }

                          return <SpeedDial
                              key={`${elementId}_currentDial`}
                              ref={currentDial}
                              className="yn-map-dial"
                              radius={36}
                              type="circle"
                              mask={false}
                              model={menuItems}
                          />;
                      }
                      else {
                          return null;
                      }
                  },
                  [
                      elementId,
                      onChange,
                      onIntermediateChange,
                      JSON.stringify( originalValue ),
                      presentation,
                      useHookForm
                  ]
              ),
              searchDialEl                              = useMemo(
                  () => {
                      if ( presentation === 'map' ) {
                          const menuItems : MenuItem[] = [
                              {
                                  label    : __( 'YN.base.form.coordinateField.contextMenu.change' ),
                                  icon     : 'pi pi-check',
                                  className: 'yn-map-dial-button yn-map-dial-button-change',
                                  command  : ( event : MenuItemCommandEvent ) => {
                                      if ( outValue.current !== searchCoordinate ) {
                                          if ( searchCoordinate && searchCoordinate.length === 2 ) {
                                              const resolver = async () => {
                                                  const rA = await resolveAddress( searchCoordinate );

                                                  setResolvedAddress( rA );
                                                  setSearchString( rA );
                                                  setSearchCoordinate( undefined );
                                                  lastSearchString.current = rA;
                                              };

                                              void resolver();
                                          }

                                          setElementValue( searchCoordinate );

                                          if ( useHookForm ) {
                                              onIntermediateChange?.( searchCoordinate, event.originalEvent );

                                              if ( timeoutId.current ) {
                                                  clearTimeout( timeoutId.current );
                                              }
                                              timeoutId.current = setTimeout( onChange.bind( null, searchCoordinate, event.originalEvent ), useHookFormDelay );

                                          }
                                          else {
                                              onChange( searchCoordinate, event.originalEvent );
                                          }
                                      }
                                  }
                              },
                              {
                                  label  : __( 'YN.base.form.coordinateField.contextMenu.clear' ),
                                  icon   : 'pi pi-times',
                                  command: ( event : MenuItemCommandEvent ) => {
                                      setSearchCoordinate( undefined );
                                      setSearchString( resolvedAddress );
                                  }
                              }
                          ];

                          return <SpeedDial
                              key={`${elementId}_searchDial`}
                              ref={searchDial}
                              className="yn-map-dial"
                              radius={36}
                              type="circle"
                              mask={false}
                              model={menuItems}
                          />;
                      }
                      else {
                          return null;
                      }
                  },
                  [
                      elementId,
                      onChange,
                      onIntermediateChange,
                      presentation,
                      resolvedAddress,
                      searchCoordinate,
                      useHookForm
                  ]
              ),
              searchField                               = useMemo(
                  () => {
                      return <SearchField
                          uid={`${elementId}_search`}
                          value={searchString}
                          onChange={onSearchChange}
                          onClear={onSearchClear}
                          onSearch={onSearch}
                          onFocus={onSearchFocus}
                          onBlur={onSearchBlur}
                          useHookForm={false}
                          className={`yn-map-search ${searchFocus ? 'yn-focus' : ''}`}
                      />;
                  },
                  [
                      elementId,
                      onSearch,
                      onSearchBlur,
                      onSearchChange,
                      onSearchClear,
                      onSearchFocus,
                      searchFocus,
                      searchString
                  ]
              ),
              fieldContent                              = useMemo(
                  () => {
                      const latInputClassNames : string[]  = [ 'yn-subfield', 'yn-coordinatefield-lat' ],
                            longInputClassNames : string[] = [ 'yn-subfield', 'yn-coordinatefield-long' ];

                      let retVal : React.ReactNode = null;

                      if ( presentation === 'field' && !rawMode ) {

                          if ( markDirty && elementValue?.join( ',' ) !== originalValue.join( ',' ) ) {
                              if ( value[ 0 ] !== originalValue[ 0 ] ) {
                                  latInputClassNames.push( 'yn-dirty-border' );
                              }

                              if ( value[ 1 ] !== originalValue[ 1 ] ) {
                                  longInputClassNames.push( 'yn-dirty-border' );
                              }
                          }

                          retVal = <div id={elementId} className="yn-coordinatefield">
                              <div className="field">
                                  <label
                                      htmlFor={`${elementId}_lat`}
                                      className="block"
                                  >
                                      Latitude
                                  </label>
                                  <InputNumber
                                      {..._.pick( props, inputNumberProps ) as InputNumberProps}

                                      inputId={`${elementId}_lat`}

                                      value={elementValue?.[ 0 ]}

                                      onChange={useHookForm ? onHookFormLatitudeInputChange : onLatitudeInputChange}

                                      pt={{
                                          input: {
                                              root: {
                                                  onFocus: onInputFocus,
                                                  onBlur : onInputBlur
                                              }
                                          }
                                      }}

                                      disabled={disabled}
                                      className={`${inputClassNames.join( ' ' )} ${latInputClassNames.join( ' ' )}`}
                                      style={{ ...defaultProps.inputStyle, ...inputStyle }}

                                      tooltip={undefined}
                                      tooltipOptions={undefined}

                                      aria-describedby={`${elementId}_message`}

                                      useGrouping={false}
                                      mode="decimal"
                                      maxFractionDigits={15}
                                  />
                              </div>
                              <div className="field">
                                  <label
                                      htmlFor={`${elementId}_long`}
                                      className="block"
                                  >
                                      Longitude
                                  </label>
                                  <InputNumber
                                      {..._.pick( props, inputNumberProps ) as Omit<InputNumberProps, 'onChange'>}

                                      inputId={`${elementId}_long`}

                                      value={elementValue?.[ 1 ]}

                                      onChange={useHookForm ? onHookFormLongitudeInputChange : onLongitudeInputChange}

                                      pt={{
                                          input: {
                                              root: {
                                                  onFocus: onInputFocus,
                                                  onBlur : onInputBlur
                                              }
                                          }
                                      }}

                                      disabled={disabled}
                                      className={`${inputClassNames.join( ' ' )} ${longInputClassNames.join( ' ' )}`}
                                      style={{ ...defaultProps.inputStyle, ...inputStyle }}

                                      tooltip={undefined}
                                      tooltipOptions={undefined}

                                      aria-describedby={`${elementId}_message`}

                                      useGrouping={false}
                                      mode="decimal"
                                      maxFractionDigits={15}
                                  />
                              </div>
                          </div>;
                      }
                      else if ( presentation === 'field' ) {
                          retVal = <InputText
                              {..._.pick( props, inputTextProps ) as Omit<InputTextProps, 'onChange'>}

                              type="text"

                              id={elementId}

                              value={value.join( ',' )}

                              pt={{
                                  root: {
                                      onChange: useHookForm ? onHookFormInputChange : onInputChange,
                                      onFocus : onInputFocus,
                                      onBlur  : onInputBlur
                                  }
                              }}

                              disabled={disabled}
                              className={`yn-coordinatefield ${inputClassNames.join( ' ' )}`}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}

                              placeholder={resolvedPlaceholder}
                              tooltip={undefined}
                              tooltipOptions={undefined}

                              aria-describedby={`${elementId}_message`}

                              keyfilter={/[0-9,.]/}
                          />;
                      }
                      else if ( presentation === 'map' ) {
                          const markers : Marker[]    = [],
                                classNames : string[] = [ 'yn-coordinatefield-map' ].concat( inputClassNames );

                          if ( currentMarker ) {
                              markers.push( currentMarker );
                          }
                          if ( searchMarker ) {
                              markers.push( searchMarker );
                          }

                          if ( disabled ) {
                              classNames.push( 'yn-disabled' );
                          }
                          if ( focus ) {
                              classNames.push( 'yn-focus' );
                          }
                          if ( JSON.stringify( originalValue ) !== JSON.stringify( outValue.current ) ) {
                              classNames.push( 'yn-dirty' );
                          }

                          retVal = <div
                              key={`${elementId}_map_wrapper`}
                              className={classNames.join( ' ' )}
                              style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                          >
                              {currentDialEl}
                              {searchDialEl}
                              {/* @ts-expect-error forwarded components default props bug*/}
                              <Map
                                  key={`${elementId}_map`}
                                  uid={`${elementId}_map`}

                                  ref={map}

                                  schema={'DATA'}
                                  dataContext={elementId}
                                  viewKey="MapSearch"
                                  layerType={mapLayerType}
                                  markerIconClassName=""
                                  externalMarkers={[
                                      {
                                          id                      : 'markerGroup_0',
                                          dataSetId               : 0,
                                          label                   : __( 'YN.base.form.coordinateField.markerGroup.label' ),
                                          color                   : '#0095ce',
                                          markers,
                                          drawConnectionsByConnect: false,
                                          drawConnectionsByTime   : false
                                      }
                                  ]}
                                  onSetCoordinateByClick={onMapCoordinate}
                              />
                              {searchField}
                          </div>;
                      }

                      return retVal;
                  },
                  [
                      currentDialEl,
                      currentMarker,
                      disabled,
                      elementId,
                      elementValue,
                      focus,
                      heightStyle,
                      inputClassNames,
                      inputStyle,
                      mapLayerType,
                      markDirty,
                      onHookFormInputChange,
                      onHookFormLatitudeInputChange,
                      onHookFormLongitudeInputChange,
                      onInputBlur,
                      onInputChange,
                      onInputFocus,
                      onLatitudeInputChange,
                      onLongitudeInputChange,
                      originalValue,
                      presentation,
                      rawMode,
                      resolvedPlaceholder,
                      searchDialEl,
                      searchMarker,
                      searchField,
                      uid,
                      useHookForm
                  ]
              );

        inValue.current = value;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    const resolver = async () => {
                        const rA = await resolveAddress( value );

                        setResolvedAddress( rA );
                        setSearchString( rA );
                        lastSearchString.current = rA;
                    };

                    setElementValue( value );
                    void resolver();
                }
            },
            [
                JSON.stringify( value )
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={!rawMode && presentation !== 'map' && markDirty && value.join( ',' ) !== originalValue.join( ',' )}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            {fieldContent}
        </FieldWrapper>;
    }
};
CoordinateField.defaultProps = defaultProps;
CoordinateField.displayName = 'CoordinateField';

export default CoordinateField;
